/**
 * MockupRenderer Component
 *
 * Renders a product mockup using CSS-based layer compositing.
 * This approach avoids CORS issues with external images.
 *
 * Layer Stack (bottom to top), following the Shirtigo Mockup Factory:
 * Layer 0: Background Color (ProductColor.hexCode) — masked to product silhouette only
 * Orderable stack per template.layerOrder (default heathered → design → base → texture):
 *   - Heathered Overlay (optional, for melange fabrics - uses Shirtigo fallback if not defined)
 *   - Design Canvas (from Fabric.js export)
 *   - Base Image (MockupTemplate.imageUrl): a pure shading map (semi-transparent
 *     dark pixels along folds and seams). It sits ABOVE the design so folds
 *     darken the artwork; below it the artwork would look pasted on. Opacity
 *     comes from template.baseOpacity (Shirtigo "Deckkraft Schatten-Layer").
 *     For embroidery the base drops directly below the design — thread must
 *     not be darkened by folds (`isEmbroidery`).
 *   - Texture Overlay (optional, for fabric structure)
 * Shadow Overlay (optional, pinned above the ordered stack: frames, drawstrings —
 *          always below the cutout mask)
 *
 * The product color layer only ever renders when a cutout mask is available —
 * without one we don't know the product's silhouette, so no color is shown
 * rather than bleeding a full-rect tint across the whole image area.
 */

import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  UIMockupLayers,
  UIProductMockupTemplate,
} from '@/lib/product-transformer';
import type { MockupBlendMode } from '@/api/products';
import {
  resolveMockupLayerOrder,
  type AdaptedPlacement,
} from '@designdeck/shared';
import { cn } from '@/lib/utils';
import { cutoutMaskStyles, useAlphaMask } from '@/lib/alphaMask';
import { HEATHER_PATTERN_FALLBACK } from '@/utils/mockupLayers';
import { isImageLoaded, markImageLoaded } from '@/lib/imageLoadCache';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface MockupRendererProps {
  /** Product ID (for debugging) */
  productId?: string;

  /**
   * The mockup template to render. A layers-only template (the admin
   * walkthrough before calibration) draws the product without a design.
   */
  template: UIProductMockupTemplate | UIMockupLayers | null;

  /** Background color (usually from ProductColor.hexCode) */
  backgroundColor?: string;

  /** Design image data URL (export from Fabric.js canvas) */
  designDataUrl?: string | null;

  /**
   * Adapted design placement for this product (from useAdaptedPreviews).
   * When provided, uses CSS sub-positioning within the PrintArea
   * instead of filling the entire PrintArea with the preview.
   */
  adaptedPlacement?: AdaptedPlacement | null;

  /**
   * Tight-crop data URL of design objects only (transparent background).
   * Used together with adaptedPlacement for adapted positioning.
   */
  designCropUrl?: string | null;

  /** Whether the current color is heathered/melange */
  isHeathered?: boolean;

  /**
   * Whether the product is decorated by embroidery. Thread must not be
   * darkened by folds, so the shading base drops directly below the design.
   */
  isEmbroidery?: boolean;

  /** Container width */
  width?: number;

  /** Container height */
  height?: number;

  /** Simplified mode - only base image + background, no design */
  simplified?: boolean;

  /** Skip applying the mask (when parent already applies it) */
  skipMask?: boolean;

  /** Use lazy loading for images (for thumbnails/cards) */
  lazyLoad?: boolean;

  /**
   * Which size of the template layers to load. `card` takes the 480 px
   * variants of base and cutout mask where they exist (product cards);
   * `full` always loads the creator assets; `rendering` takes the high-res
   * rendering assets where they exist, as the creator's editor canvas does
   * (createCanvasConfig) for the one large mockup it shows.
   */
  assetSize?: 'full' | 'card' | 'rendering';

  /** Fetch priority: 'high' for Creator mockup, 'low' for ProductCards */
  priority?: 'high' | 'low' | 'auto';
  /** Colour behind the product layers; defaults to the card surface. */
  frameBackground?: string;

  /** Additional CSS classes */
  className?: string;

  /** Called when rendering completes */
  onRenderComplete?: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// BLEND MODE MAPPING (CSS mix-blend-mode)
// ═══════════════════════════════════════════════════════════════════

/**
 * Map API blend modes to CSS mix-blend-mode values
 */
function mapBlendMode(blendMode: MockupBlendMode): string {
  const blendMap: Record<MockupBlendMode, string> = {
    NORMAL: 'normal',
    MULTIPLY: 'multiply',
    SCREEN: 'screen',
    OVERLAY: 'overlay',
    SOFT_LIGHT: 'soft-light',
    HARD_LIGHT: 'hard-light',
  };
  return blendMap[blendMode] || 'normal';
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const MockupRenderer = memo(function MockupRenderer({
  template,
  backgroundColor = '#FFFFFF',
  designDataUrl,
  adaptedPlacement,
  designCropUrl,
  isHeathered = false,
  isEmbroidery = false,
  width = 520,
  height = 620,
  simplified = false,
  skipMask = false,
  lazyLoad = false,
  assetSize = 'full',
  priority: _priority = 'auto',
  frameBackground = 'var(--paper-2, #ffffff)',
  className,
  onRenderComplete,
}: MockupRendererProps) {
  const { t } = useTranslation();
  const [baseLoaded, setBaseLoaded] = useState(() =>
    isImageLoaded(template?.imageUrl ?? '')
  );
  const [baseError, setBaseError] = useState(false);

  // The same MockupRenderer instance can be re-used across template changes
  // (e.g. a product's mockup data reloads while its card stays mounted), so
  // re-sync baseLoaded from the cache whenever the base image URL changes.
  // baseError belongs to the previous URL and must not outlive it, otherwise
  // the error state would swallow a working replacement image.
  // The card-sized or rendering layers where asked for and available; the
  // full ones else.
  const baseImageUrl =
    (assetSize === 'card' ? template?.cardImageUrl : null) ??
    (assetSize === 'rendering' ? template?.renderingImageUrl : null) ??
    template?.imageUrl ??
    '';
  const cutoutMaskUrl =
    (assetSize === 'card' ? template?.cardCutoutMaskUrl : null) ??
    (assetSize === 'rendering' ? template?.renderingCutoutMaskUrl : null) ??
    template?.cutoutMaskUrl ??
    null;

  useEffect(() => {
    setBaseLoaded(isImageLoaded(baseImageUrl));
    setBaseError(false);
  }, [baseImageUrl]);

  // ─────────────────────────────────────────────────────────────────
  // RENDER: No Template
  // ─────────────────────────────────────────────────────────────────
  // Converted to alpha masks: Safari on iOS ignores the luminance mask mode
  // (see lib/alphaMask.ts) and would show a filled square instead.
  const cutoutMask = useAlphaMask(skipMask ? null : cutoutMaskUrl);
  const toplayerMask = useAlphaMask(
    skipMask ? null : template?.toplayerMaskUrl
  );

  if (!template) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-paper-2 rounded-control',
          className
        )}
        style={{ width, height }}
      >
        <span className="text-mute text-sm">{t('creator.mockup.none')}</span>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // RENDER: Error State
  // ─────────────────────────────────────────────────────────────────
  if (baseError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] rounded-control',
          className
        )}
        style={{ width, height }}
      >
        <span className="text-danger text-sm">
          {t('creator.mockup.loadFailed')}
        </span>
      </div>
    );
  }

  // Native mockup dimensions: the coordinate system of the template's
  // print area rectangle.
  const mockupWidth = template.mockupWidthPx;
  const mockupHeight = template.mockupHeightPx;

  // The print area on this template (templateRect, R7). A layers-only
  // template has none, and then no design is drawn.
  const printAreaRect =
    'printAreaRect' in template ? template.printAreaRect : null;

  // ─────────────────────────────────────────────────────────────────
  // PIXEL-BASED POSITIONING
  // ─────────────────────────────────────────────────────────────────
  // The base image uses object-contain, so we need to calculate the actual
  // rendered size and scale factor to position the design correctly.
  // The design image is exported at the print area's pixel size, so it is
  // scaled and positioned to where the print area appears on the scaled
  // base mockup image.

  // Calculate scale factor (object-contain scales uniformly)
  // Use the smaller scale to ensure mockup fits (object-contain behavior)
  const scaleX = width / mockupWidth;
  const scaleY = height / mockupHeight;
  const scaleFactor = Math.min(scaleX, scaleY);

  // Calculate rendered mockup dimensions using uniform scale
  const renderedMockupWidth = mockupWidth * scaleFactor;
  const renderedMockupHeight = mockupHeight * scaleFactor;

  // Calculate offset for object-contain centering
  const mockupOffsetX = (width - renderedMockupWidth) / 2;
  const mockupOffsetY = (height - renderedMockupHeight) / 2;

  // Position and size the design at the scaled print area rectangle; the
  // browser handles sub-pixel rendering.
  const designLeft = mockupOffsetX + (printAreaRect?.x ?? 0) * scaleFactor;
  const designTop = mockupOffsetY + (printAreaRect?.y ?? 0) * scaleFactor;
  const designWidth = (printAreaRect?.width ?? 0) * scaleFactor;
  const designHeight = (printAreaRect?.height ?? 0) * scaleFactor;
  const showsDesign = !simplified && printAreaRect !== null;

  const designStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${designTop}px`,
    left: `${designLeft}px`,
    width: `${designWidth}px`,
    height: `${designHeight}px`,
  };

  // Determine heather overlay URL with fallback
  const heatherOverlayUrl = isHeathered
    ? template.heatheredOverlayUrl || HEATHER_PATTERN_FALLBACK
    : null;

  const hasCutoutMask = !skipMask && !!cutoutMaskUrl;

  // Match CanvasEditor: apply cutout mask + product color on the layer wrapper
  // so the color fills the silhouette and the base mockup renders on top.
  const productLayerStyle: React.CSSProperties =
    hasCutoutMask && cutoutMask
      ? { ...cutoutMaskStyles(cutoutMask), backgroundColor }
      : {};

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={{
        width,
        height,
        isolation: 'isolate',
        // The card surface by default; callers whose tile is a photo well
        // pass var(--photo) so the white mockup base merges with the tile.
        backgroundColor: frameBackground,
      }}
    >
      {/* Product layer stack, rendered bottom → top per template.layerOrder
          (heathered, design, base, texture) inside the masked wrapper; for
          embroidery the shading base drops directly below the design */}
      <div className="absolute inset-0" style={productLayerStyle}>
        {resolveMockupLayerOrder(template.layerOrder, {
          shadingBelowDesign: isEmbroidery,
        }).map((layer) => {
          switch (layer) {
            case 'base':
              return (
                <div key="base" className="contents">
                  {/* Shading map of the garment (Shirtigo "Schatten-Layer") */}
                  <img
                    src={baseImageUrl}
                    alt={t('creator.mockup.productAlt')}
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{ opacity: template.baseOpacity ?? 1 }}
                    loading={lazyLoad ? 'lazy' : 'eager'}
                    decoding={lazyLoad ? 'async' : 'sync'}
                    onLoad={() => {
                      markImageLoaded(baseImageUrl);
                      setBaseLoaded(true);
                      onRenderComplete?.();
                    }}
                    onError={() => setBaseError(true)}
                  />
                </div>
              );
            case 'design':
              return (
                <div key="design" className="contents">
                  {/* Layer 2: Design Canvas (if not simplified) */}
                  {showsDesign &&
                  baseLoaded &&
                  adaptedPlacement &&
                  designCropUrl ? (
                    // PRIMARY PATH (Stufe 4): Adapted mode using design crop + adaptation placement.
                    // Works without a live Fabric.js canvas -- deterministic, resolution-independent.
                    <div style={designStyle} className="overflow-hidden">
                      <div
                        style={{
                          position: 'absolute',
                          left: `${adaptedPlacement.relLeft * 100}%`,
                          top: `${adaptedPlacement.relTop * 100}%`,
                          width: `${adaptedPlacement.relWidth * 100}%`,
                          height: `${adaptedPlacement.relHeight * 100}%`,
                        }}
                      >
                        <img
                          src={designCropUrl}
                          alt={t('creator.mockup.designAlt')}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'fill',
                            imageRendering: 'crisp-edges',
                          }}
                        />
                      </div>
                    </div>
                  ) : showsDesign &&
                    baseLoaded &&
                    adaptedPlacement &&
                    designDataUrl &&
                    adaptedPlacement.relWidth > 0 &&
                    adaptedPlacement.relHeight > 0 ? (
                    // ADAPTED POSITION WITH FULL CANVAS: placement known but crop not ready.
                    // Show the correct region of the full print-area export so position is correct in sidebar/product list.
                    <div style={designStyle} className="overflow-hidden">
                      <div
                        style={{
                          position: 'absolute',
                          left: `${adaptedPlacement.relLeft * 100}%`,
                          top: `${adaptedPlacement.relTop * 100}%`,
                          width: `${adaptedPlacement.relWidth * 100}%`,
                          height: `${adaptedPlacement.relHeight * 100}%`,
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={designDataUrl}
                          alt={t('creator.mockup.designAlt')}
                          style={{
                            position: 'absolute',
                            display: 'block',
                            width: `${100 / adaptedPlacement.relWidth}%`,
                            height: `${100 / adaptedPlacement.relHeight}%`,
                            left: `${(-adaptedPlacement.relLeft / adaptedPlacement.relWidth) * 100}%`,
                            top: `${(-adaptedPlacement.relTop / adaptedPlacement.relHeight) * 100}%`,
                            objectFit: 'fill',
                            imageRendering: 'crisp-edges',
                          }}
                        />
                      </div>
                    </div>
                  ) : showsDesign && designDataUrl && baseLoaded ? (
                    // FALLBACK: Standard mode filling entire PrintArea with canvas export.
                    // Used when no adapted placement (e.g. first render, legacy data).
                    <div style={designStyle} className="overflow-hidden">
                      <img
                        src={designDataUrl}
                        alt={t('creator.mockup.designAlt')}
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          objectFit: 'fill',
                          imageRendering: 'crisp-edges',
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              );
            case 'heathered':
              return (
                <div key="heathered" className="contents">
                  {/* Layer 3: Heathered Overlay (only if color is heathered, with fallback pattern) */}
                  {heatherOverlayUrl && baseLoaded && (
                    <img
                      src={heatherOverlayUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                      style={{
                        mixBlendMode: mapBlendMode(
                          template.heatheredBlendMode
                        ) as React.CSSProperties['mixBlendMode'],
                        opacity: template.heatheredOpacity,
                      }}
                    />
                  )}
                </div>
              );
            case 'texture':
              return (
                <div key="texture" className="contents">
                  {/* Layer 4: Texture Overlay */}
                  {template.textureOverlayUrl && baseLoaded && (
                    <img
                      src={template.textureOverlayUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                      style={{
                        mixBlendMode: mapBlendMode(
                          template.textureBlendMode
                        ) as React.CSSProperties['mixBlendMode'],
                        opacity: template.textureOpacity,
                      }}
                    />
                  )}
                </div>
              );
            default:
              return null;
          }
        })}

        {/* Layer 5: Shadow Overlay — pinned above the ordered stack, clipped
            by the cutout mask on the wrapper like everything else */}
        {template.shadowOverlayUrl && baseLoaded && (
          <img
            src={template.shadowOverlayUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            style={{
              mixBlendMode: mapBlendMode(
                template.shadowBlendMode
              ) as React.CSSProperties['mixBlendMode'],
              opacity: template.shadowOpacity,
            }}
          />
        )}
      </div>

      {/* Drawstrings and the like — garment parts that hang in front of the
          print. Built the way the garment itself is (product colour with the
          shading map over it), clipped to the cord mask and drawn above the
          whole stack, so a design cannot run across them. Taking the shading
          map alone would not work: there the cords carry the same alpha as
          the fabric around them. Shirtigo does the same in its top layer
          ("Add Drawstring"). Inside the masked wrapper, because the cords
          belong to the silhouette. */}
      {template.toplayerMaskUrl && baseLoaded && (
        <div
          data-layer="toplayer"
          className="absolute inset-0 pointer-events-none"
          style={{
            ...(toplayerMask ? cutoutMaskStyles(toplayerMask) : {}),
            backgroundColor,
          }}
        >
          <img
            src={baseImageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            style={{ opacity: template.baseOpacity ?? 1 }}
          />
        </div>
      )}

      {/* Overlay — above the whole stack AND above the cutout mask: a sibling
          of the masked wrapper so frames, clips and hangers are never clipped */}
      {template.topOverlayUrl && baseLoaded && (
        <img
          src={template.topOverlayUrl}
          alt=""
          data-layer="overlay"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        />
      )}

      {/* Loading skeleton - outside product layers so card background stays white */}
      {!baseLoaded && !baseError && (
        <div className="absolute inset-0 bg-gradient-to-br from-paper-2 to-paper-3 animate-pulse">
          <div className="absolute inset-[15%] bg-paper-3/50 rounded-control" />
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════
// SIMPLIFIED VERSION FOR THUMBNAILS
// ═══════════════════════════════════════════════════════════════════

interface MockupThumbnailProps {
  template: UIProductMockupTemplate | null;
  backgroundColor?: string;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Simplified mockup renderer for thumbnails
 * Renders the product color, base image, cutout mask and the texture/shadow
 * overlays, but no design layer. The heathered overlay stays off because the
 * thumbnail does not know the colour. Uses lazy loading and low priority for
 * better initial page load performance.
 */
export const MockupThumbnail = memo(function MockupThumbnail({
  template,
  backgroundColor = '#FFFFFF',
  width = 100,
  height = 120,
  className,
}: MockupThumbnailProps) {
  return (
    <MockupRenderer
      template={template}
      backgroundColor={backgroundColor}
      width={width}
      height={height}
      simplified={true}
      lazyLoad={true}
      priority="low"
      assetSize="card"
      className={className}
    />
  );
});

export default MockupRenderer;
